import { useToast } from '@helix-ds/react'
import * as Sentry from '@sentry/react'
import React, { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getOrderedLocales } from '../../../i18n/orderedLocales'
import { LOCALE_CATALOG, type SupportedLocale } from '../../../i18n/types'
import { usePreferencesContext } from '../usePreferencesContext'

const LanguageSelector: React.FC = () => {
  const intl = useIntl()
  const { state, updateLanguage } = usePreferencesContext()
  const { addNotification } = useToast()
  const [saving, setSaving] = useState(false)

  if (state.status !== 'ready' || !state.available) {
    return null
  }

  const current = state.locale

  async function handleChange(next: SupportedLocale) {
    if (next === current || saving) return
    setSaving(true)
    try {
      await updateLanguage(next)
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: 'preferences', action: 'patch-language' } })
      addNotification({
        type: 'error',
        title: intl.formatMessage({
          id: 'languageSelector.toast.errorTitle',
          defaultMessage: 'Could not save language',
        }),
        message: intl.formatMessage({
          id: 'languageSelector.toast.errorMessage',
          defaultMessage: 'We could not update your language preference. Please try again.',
        }),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <section aria-labelledby="language-selector-heading" className="mt-2">
      <h3 id="language-selector-heading" className="text-text-heading mb-4 text-lg font-semibold dark:text-white">
        <FormattedMessage id="profile.languageSelector.heading" defaultMessage="Preferred language" />
      </h3>
      <select
        value={current}
        disabled={saving}
        onChange={(e) => handleChange(e.target.value as SupportedLocale)}
        className="border-border-base text-text-heading focus:ring-brand-600/20 focus:border-brand-600 w-full rounded-xl border bg-white px-3 py-2.5 text-sm font-medium focus:ring-2 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white"
        aria-labelledby="language-selector-heading"
      >
        {getOrderedLocales().map((code) => (
          <option key={code} value={code}>
            {LOCALE_CATALOG[code].endonym}
          </option>
        ))}
      </select>
    </section>
  )
}

export default LanguageSelector
